import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, CalendarCheck } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, CLINIC, telLink } from "../data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const linkBase = "relative text-sm font-medium tracking-wide transition-colors duration-300";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/85 border-b transition-all duration-500 ${
        scrolled
          ? "border-dash-line/70 shadow-[0_8px_30px_rgb(10,37,64,0.06)]"
          : "border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between h-[74px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
          <Logo className="h-10 w-10 transition-transform duration-500 group-hover:rotate-[-8deg]" />
          <div className="leading-tight">
            <div className="font-display font-bold text-dash-ink text-[13px] sm:text-[15px] tracking-tight">DASH Physiotherapy</div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.14em] text-dash-teal font-accent font-semibold">&amp; Speech and Hearing Clinic</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-dash-teal" : "text-dash-ink/80 hover:text-dash-teal"}`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute -bottom-1.5 left-0 h-[2px] rounded-full bg-dash-teal transition-all duration-300 ${isActive ? "w-full" : "w-0"}`} />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <a
            href={telLink}
            data-testid="nav-call-btn"
            aria-label={`Call ${CLINIC.phone}`}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full bg-dash-ink/5 text-dash-ink hover:bg-dash-teal hover:text-white transition-colors duration-300"
          >
            <Phone className="h-[18px] w-[18px]" />
          </a>
          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-book-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-dash-teal px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-dash-teal/25 transition-all duration-300 hover:bg-dash-ink hover:-translate-y-0.5"
          >
            <CalendarCheck className="h-4 w-4" />
            Book an Appointment
          </button>
          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full bg-dash-ink text-white"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-dash-ink/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 260 }}
              data-testid="mobile-menu-panel"
            >
              <div className="flex items-center justify-between px-6 h-[74px] border-b border-dash-line">
                <div className="flex items-center gap-2.5">
                  <Logo className="h-9 w-9" />
                  <div className="leading-tight">
                    <div className="font-display font-bold text-dash-ink text-sm">DASH Physiotherapy</div>
                    <div className="text-[9px] uppercase tracking-[0.14em] text-dash-teal font-accent font-semibold">&amp; Speech and Hearing Clinic</div>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="h-10 w-10 rounded-full bg-dash-ink/5 inline-flex items-center justify-center">
                  <X className="h-5 w-5 text-dash-ink" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i + 0.1 }}
                  >
                    <NavLink
                      to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={({ isActive }) =>
                        `block py-3 text-lg font-display font-semibold border-b border-dash-line/70 ${isActive ? "text-dash-teal" : "text-dash-ink"}`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
              </div>
              <div className="p-6 border-t border-dash-line space-y-3">
                <a href={telLink} data-testid="mobile-call-link" className="flex items-center justify-center gap-2 rounded-full bg-dash-ink/5 text-dash-ink py-3 font-semibold">
                  <Phone className="h-4 w-4" /> {CLINIC.phone}
                </a>
                <button
                  onClick={() => { setOpen(false); navigate("/contact"); }}
                  data-testid="mobile-book-btn"
                  className="w-full flex items-center justify-center gap-2 rounded-full bg-dash-teal text-white py-3 font-semibold"
                >
                  <CalendarCheck className="h-4 w-4" /> Book an Appointment
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
